import { ResponseStatus } from './constants';
import { getAccountHistory } from './services/account-history';
import { fetchActiveStakes, fetchHistoryStakes } from './services/active-stakes';
import { loginUser, loginUserAfterRedirect, logoutUser } from './services/auth';
import { checkAvailability } from './services/bet-slip/check-availability';
import { getCashbackExperienceHistory } from './services/cashback/cashback-experience-history';
import { getClientInfo } from './services/client/client-info';
import { changeLanguage, getProfile, savePersonalData } from './services/client/client-profile';
import { emailConfirm, resendCode, sendCode } from './services/client/user-contacts-setting/email-confirm';
import { phoneConfirm, sendPhoneCode } from './services/client/user-contacts-setting/phone-confirm';
import { getAllExternalGames, getExternalGameBannerById, getExternalGameById } from './services/external-games';
import { addToFavorites, removeFromFavorites } from './services/favorites';
import { getFreebets } from './services/freebets';
import { sendFreshChatRestoreId } from './services/fresh-chat';
import { getCities } from './services/geoinformation';
import { getLiveSchedule } from './services/live/schedule';
import { getInterestingMatches } from './services/matches/interesting-matches';
import { getLive, getLiveMainLine } from './services/matches/live';
import { getMatchResult, getMatchResultDetails, getMatchResultSports } from './services/matches/match-result';
import { getMatchStatus } from './services/matches/match-status';
import { getPrematchCategories } from './services/matches/prematch-categories';
import { getPrematchContent } from './services/matches/prematch-content';
import { getAllNotifications, getUnreadNotifications, getUnreadNotificationsCount, markReadAllNotifications } from './services/notifications';
import { deleteWithdrawalActive, getWithdrawalActive } from './services/payment';
import { checkPromo } from './services/promo';
import { getCheckCode, sendCheckCode, sendCheckCodeAuth, sendNewPassword } from './services/registration';
import { getSearchedValue } from './services/search';
import { changeSiteConfig, fetchSiteConfigs } from './services/site-configs';
import { getBigStakes } from './services/stakes/big-stakes';
import { cashOutAmount } from './services/stakes/cash-out';
import { getStakeBalance } from './services/user-account/stake-balance';
import { getWithdrawalBalance } from './services/user-account/withdrawalBalance';
import { strapiServices } from './services-strapi';
import useFetch from './useFetch';
import { convertActionToPreviousStructure } from './utils/convert-action-to-prev-structure';
import type {
  ServiceConfig,
  ServiceError,
  ServiceInstance,
  ServiceRequestConfig,
  ServiceResponse,
} from './utils/create-http-api-service';
import {
  addSelectedLocal,
  createHttpApiService,
  httpApiService,
  HttpApiServiceContext,
  isCancelError,
  isHttpApiServiceError,
  useHttpApiServiceContext,
} from './utils/create-http-api-service';
import type { ErrorPayload as AdapterErrorPayload } from './utils/default-error-adapter';
import { getAsyncThunk } from './utils/get-async-thunk';

// ---

// Если сервис используется через useFetch или getAsyncThunk,
// то он должен принимать первым параметром (config: ServiceConfig).
// Чтобы успешно отработать во время SSR.
export const API = {
  strapiServices,
  accountHistory: {
    getAccountHistory,
  },
  live: {
    schedule: {
      getLiveSchedule,
    },
  },
  externalGames: {
    getExternalGameById,
    getAllExternalGames,
    getExternalGameBannerById,
  },
  geoinformation: {
    getCities,
  },
  siteConfigs: {
    fetchSiteConfigs,
    changeSiteConfig,
  },
  registration: {
    getCheckCode,
    sendCheckCodeAuth,
    sendCheckCode,
    sendNewPassword,
  },
  promo: {
    checkPromo,
  },
  freebets: {
    getFreebets,
  },
  myStakes: {
    fetchActiveStakes,
    fetchHistoryStakes,
  },
  payment: {
    getWithdrawalActive,
    deleteWithdrawalActive,
  },
  stakes: {
    getBigStakes,
    cashOutAmount,
  },
  matches: {
    getMatchStatus,
    getMatchResultSports,
    getMatchResultDetails,
    getMatchResult,
    getInterestingMatches,
    getPrematchCategories,
    getPrematchContent,
    getLiveMainLine,
    getLive,
  },
  client: {
    clientInfo: {
      getClientInfo,
    },
    clientProfile: {
      getProfile,
      changeLanguage,
      savePersonalData,
    },
    confirmPhone: {
      sendPhoneCode,
      phoneConfirm,
    },
    confirmEmail: {
      sendCode,
      resendCode,
      emailConfirm,
    },
  },
  userAccount: {
    stakeBalance: {
      getStakeBalance,
    },
    withdrawalBalance: {
      getWithdrawalBalance,
    },
  },
  cashback: {
    getCashbackExperienceHistory,
  },
  betSlip: {
    checkAvailability,
  },
  search: {
    getSearchedValue,
  },
  auth: {
    logoutUser,
    loginUser,
    loginUserAfterRedirect,
  },
  notifications: {
    getAllNotifications,
    getUnreadNotifications,
    getUnreadNotificationsCount,
    markReadAllNotifications,
  },
  freshChat: {
    sendFreshChatRestoreId,
  },
  favorites: {
    addToFavorites,
    removeFromFavorites,
  },
};

// ---

export type ErrorPayload = AdapterErrorPayload;
export {
  ResponseStatus,
  useFetch,
  getAsyncThunk,
  convertActionToPreviousStructure,

  ServiceError,
  ServiceInstance,
  ServiceResponse,
  ServiceRequestConfig,
  ServiceConfig,
  httpApiService,
  createHttpApiService,
  isCancelError,
  isHttpApiServiceError,
  useHttpApiServiceContext,
  HttpApiServiceContext,
  addSelectedLocal,
};
